import type { PHLibraryItem } from "../../shared/planetHoppersLibrary";
import { emptyPHQuiz } from "../../shared/planetHoppers";
import { phText as t, phEscape as e } from "./planetHoppersView";

const KEY="ziarcadia:planet-hoppers:cloud:v1";
type Link={accountId:string;id:string;revision:number;source:string};
type Result={accountId:string;items?:PHLibraryItem[];item?:PHLibraryItem & {source:string}};
class CloudError extends Error { constructor(public status:number,public code:string,message:string){super(message);} }
export class PHCloudLibrary {
    private accountId="";
    private link:Link|null=null;
    private items:PHLibraryItem[]=[];
    private status=t("Checking account…","계정 확인 중…");
    private signedOut=false;
    private conflict=false;
    private disposed=false;
    private timer:ReturnType<typeof setTimeout>|null=null;
    private saving:Promise<boolean>|null=null;
    private checking:Promise<void>|null=null;
    private modal:HTMLDialogElement|null=null;
    private slot:HTMLElement|null=null;
    constructor(private source:()=>string,private open:(source:string)=>void){
        try{const link=JSON.parse(localStorage.getItem(KEY)||"null");if(link&&typeof link.accountId==="string"&&typeof link.id==="string"&&Number.isInteger(link.revision)&&typeof link.source==="string")this.link=link;}catch{}
        window.addEventListener("online",this.retry);window.addEventListener("focus",this.retry);
        void this.check();
    }
    private async api(payload?:Record<string,unknown>):Promise<Result>{
        const response=await fetch('/api/account/planet-quizzes',{method:payload?'POST':'GET',credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(15000),...(payload?{headers:{'Content-Type':'application/json'},body:JSON.stringify({...payload,accountId:this.accountId})}:{})});
        const data=await response.json();
        if(!response.ok)throw new CloudError(response.status,data.code||"",data.error||"Could not save quiz");
        return data;
    }
    private remember(){try{localStorage.setItem(KEY,JSON.stringify(this.link));}catch{}}
    private retry=()=>{if(!this.disposed)void this.check();};
    private check():Promise<void>{
        if(this.checking)return this.checking;
        this.checking=(async()=>{
            try{
                const result=await this.api();if(this.disposed)return;
                this.accountId=result.accountId;this.items=result.items||[];this.signedOut=false;
                if(this.link&&this.link.accountId!==this.accountId){this.conflict=true;this.status=t("This draft belongs to another account. Save a copy to use it here.","다른 계정의 초안입니다. 이 계정에 사본을 저장하세요.");}
                else if(!this.conflict){this.status=this.link?.source===this.source()?t("Saved to your account","계정에 저장됨"):t("Ready to save","저장 준비됨");this.changed();}
            }catch(error){this.failed(error);}finally{this.checking=null;this.paint();}
        })();return this.checking;
    }
    mount(slot:HTMLElement){this.slot=slot;this.paint();}
    changed(){
        if(this.disposed)return;
        if(this.timer)clearTimeout(this.timer);
        if(!this.accountId||this.signedOut||this.conflict)return;
        if(this.link?.source===this.source()){this.status=t("Saved to your account","계정에 저장됨");this.paint();return;}
        if(!JSON.parse(this.source()).questions.length&&!this.link)return;
        this.status=t("Saving to your account…","계정에 저장 중…");this.paint();
        this.timer=setTimeout(()=>{this.timer=null;void this.flush();},800);
    }
    private failed(error:unknown){
        if(error instanceof CloudError&&error.status===401){this.signedOut=true;this.accountId="";this.status=t("Sign in to save quizzes across computers","로그인하면 다른 컴퓨터에서도 퀴즈를 사용할 수 있어요");}
        else if(error instanceof CloudError&&error.code==="conflict"){this.conflict=true;this.status=t("Changed on another computer. Your edits are kept here; save a copy.","다른 컴퓨터에서 수정되었습니다. 현재 수정 내용은 보관됩니다. 사본을 저장하세요.");}
        else this.status=t("Not synced — saved on this computer. Retry.","동기화되지 않음 — 이 컴퓨터에 저장됨. 다시 시도하세요.");
        this.paint();
    }
    async flush():Promise<boolean>{
        if(this.timer)clearTimeout(this.timer);this.timer=null;
        if(this.saving){const ok=await this.saving;if(!ok)return false;return this.flush();}
        if(!this.accountId||this.signedOut||this.conflict)return false;
        const source=this.source();if(this.link?.source===source)return true;
        if(!this.link&&!JSON.parse(source).questions.length)return true;
        const accountId=this.accountId;
        this.link??={accountId,id:crypto.randomUUID(),revision:0,source:""};this.remember();
        const link={...this.link};
        this.saving=(async()=>{
            try{
                const result=await this.api({action:"save",id:link.id,revision:link.revision,source});
                if(result.accountId!==accountId)throw new Error("Account changed");
                this.link={accountId,id:result.item!.id,revision:result.item!.revision,source};this.remember();
                this.items=[result.item!,...this.items.filter(x=>x.id!==result.item!.id)];
                this.status=t("Saved to your account","계정에 저장됨");return true;
            }catch(error){this.failed(error);return false;}
            finally{this.saving=null;this.paint();}
        })();
        const ok=await this.saving;if(ok&&!this.disposed&&this.source()!==source)this.changed();return ok;
    }
    private paint(){
        if(!this.slot||this.disposed)return;
        this.slot.innerHTML=`<span class="ph-cloud-status" role="status">${e(this.status)}</span>${this.signedOut?`<button type="button" data-account-button>${t("Sign in","로그인")}</button>`:`<button type="button" data-cloud-retry>${this.conflict?t("Save a copy","사본 저장"):t("Save now","지금 저장")}</button>`}`;
        this.slot.querySelector('[data-cloud-retry]')?.addEventListener('click',()=>{if(this.conflict){this.link=null;this.conflict=false;this.remember();}void this.check().then(()=>this.flush());});
    }
    async showLibrary(){
        await this.check();if(this.signedOut||!this.accountId||this.disposed)return;
        this.modal?.remove();const dialog=document.createElement('dialog');dialog.className='ph-dialog ph-cloud-dialog';
        dialog.innerHTML=`<button type="button" data-cloud-close aria-label="${t("Close","닫기")}">×</button><h2>${t("My quizzes","내 퀴즈")}</h2><p>${t("Available on any computer when you sign in with the same Google account.","같은 Google 계정으로 로그인하면 어느 컴퓨터에서든 사용할 수 있습니다.")}</p><button type="button" data-cloud-new>${t("New quiz","새 퀴즈")}</button><div class="ph-cloud-list">${this.items.map(item=>`<button type="button" data-cloud-open="${e(item.id)}"><strong>${e(item.title)}</strong><span>${item.count} ${t("questions","문제")} · ${new Date(item.updatedAt).toLocaleDateString()}</span></button>`).join('')||`<p>${t("Your quizzes will appear here after saving.","저장한 퀴즈가 여기에 표시됩니다.")}</p>`}</div><p data-cloud-message role="status"></p>`;
        this.modal=dialog;document.body.append(dialog);dialog.showModal();
        dialog.querySelector('[data-cloud-close]')!.addEventListener('click',()=>dialog.close());
        dialog.addEventListener('close',()=>{dialog.remove();if(this.modal===dialog)this.modal=null;});
        const load=async(id?:string)=>{
            const buttons=dialog.querySelectorAll<HTMLButtonElement>('button');buttons.forEach(b=>b.disabled=true);
            try{
                // Never discard edits that could not be uploaded; local backup remains available.
                if(JSON.parse(this.source()).questions.length&&this.link?.source!==this.source()&&!await this.flush())throw new Error(t("Save the current quiz or download a backup before switching.","전환하기 전에 현재 퀴즈를 저장하거나 백업을 다운로드하세요."));
                if(id){const result=await this.api({action:"get",id});if(this.disposed)return;const item=result.item!;this.link={accountId:result.accountId,id:item.id,revision:item.revision,source:item.source};this.remember();this.open(item.source);}
                else{this.link=null;this.remember();this.open(JSON.stringify(emptyPHQuiz()));}
                this.status=id?t("Saved to your account","계정에 저장됨"):t("New quiz","새 퀴즈");this.paint();dialog.close();
            }catch(error){dialog.querySelector('[data-cloud-message]')!.textContent=error instanceof Error?error.message:String(error);}
            finally{buttons.forEach(b=>b.disabled=false);}
        };
        dialog.querySelectorAll<HTMLButtonElement>('[data-cloud-open]').forEach(button=>button.addEventListener('click',()=>void load(button.dataset.cloudOpen)));
        dialog.querySelector('[data-cloud-new]')!.addEventListener('click',()=>void load());
    }
    destroy(){if(this.timer)clearTimeout(this.timer);void this.flush();this.disposed=true;this.modal?.close();window.removeEventListener('online',this.retry);window.removeEventListener('focus',this.retry);}
}
